const ROLE_KEY = "repman_user_role";

const SUPERUSER_KEY = "repman_is_superuser";

const USER_ID_KEY = "repman_user_id";

const USER_JSON_KEY = "repman_user_json";

const COMPANY_ID_KEY = "repman_company_id";

const IMPERSONATION_KEY = "repman_impersonation";

const OAUTH_CHECKPOINT_KEY = "repman-oauth-session-checkpoint";



export const USER_ROLE = {

  CLIENT: "user",

  ADMIN: "admin",

};



function normalizeCompany(user) {

  if (!user) return null;

  if (user.company && user.company.id != null) {

    return {

      id: user.company.id,

      company_name: user.company.company_name,

      company_email: user.company.company_email || null,

    };

  }

  const companyId = user.company_id ?? user.companyId;

  if (companyId != null) {

    return { id: companyId };

  }

  return null;

}



function buildUserSnapshot(user) {

  if (!user) return null;

  const company = normalizeCompany(user);

  return {

    id: user.id,

    email: user.email,

    full_name: user.full_name,

    role: user.role,

    is_superuser: Boolean(user.is_superuser),

    account_status: user.account_status || "trial",

    trial_ends_at: user.trial_ends_at || null,

    company,

  };

}



function oauthCheckpointStorageKey(oauthState) {

  return oauthState ? `${OAUTH_CHECKPOINT_KEY}:${oauthState}` : null;

}



function readOAuthCheckpointPayload(oauthState) {

  if (typeof window === "undefined") return null;



  if (oauthState) {

    const byState = window.localStorage.getItem(oauthCheckpointStorageKey(oauthState));

    if (byState) {

      try {

        return JSON.parse(byState);

      } catch {

        /* fall through */

      }

    }

  }



  const raw = window.sessionStorage.getItem(OAUTH_CHECKPOINT_KEY);

  if (!raw) return null;

  try {

    return JSON.parse(raw);

  } catch {

    return null;

  }

}



function writeOAuthCheckpointPayload(checkpoint, oauthState) {

  const serialized = JSON.stringify(checkpoint);

  window.sessionStorage.setItem(OAUTH_CHECKPOINT_KEY, serialized);

  const stateKey = oauthCheckpointStorageKey(oauthState);

  if (stateKey) {

    window.localStorage.setItem(stateKey, serialized);

  }

}



function applyOAuthCheckpointPayload(checkpoint) {

  if (

    checkpoint.origin &&

    typeof window !== "undefined" &&

    checkpoint.origin !== window.location.origin

  ) {

    return false;

  }



  if (checkpoint.role) {

    window.localStorage.setItem(ROLE_KEY, checkpoint.role);

  }

  if (checkpoint.userJson) {
    window.localStorage.setItem(USER_JSON_KEY, checkpoint.userJson);
    try {
      const parsed = JSON.parse(checkpoint.userJson);
      if (parsed?.company?.id != null) {
        window.localStorage.setItem(COMPANY_ID_KEY, String(parsed.company.id));
      }
    } catch {
      /* ignore */
    }
  } else if (checkpoint.companyId) {
    window.localStorage.setItem(COMPANY_ID_KEY, checkpoint.companyId);
  }

  if (checkpoint.superuser != null) {

    window.localStorage.setItem(SUPERUSER_KEY, checkpoint.superuser);

  }

  if (checkpoint.impersonation) {

    window.localStorage.setItem(IMPERSONATION_KEY, checkpoint.impersonation);

  } else {

    clearImpersonationState();

  }

  if (checkpoint.accessToken) {

    window.localStorage.setItem("repman_access_token", checkpoint.accessToken);

  }



  window.dispatchEvent(new Event("repman:session-changed"));

  return true;

}



export function getUserIdFromAccessToken(token) {

  if (!token || typeof token !== "string") return null;

  try {

    const base64 = token.split(".")[1];

    if (!base64) return null;

    const padded = base64.replace(/-/g, "+").replace(/_/g, "/");

    const payload = JSON.parse(atob(padded));

    const userId = payload?.user_id ?? payload?.sub;

    return userId != null ? Number(userId) : null;

  } catch {

    return null;

  }

}



/** Refuse to overwrite session with a different account (e.g. stale admin refresh cookie). */

export function persistAuthUser(user, options = {}) {

  if (typeof window === "undefined" || !user) return;



  const { allowUserSwitch = false } = options;

  const snapshot = buildUserSnapshot(user);

  if (!snapshot) return;



  const current = getCurrentUser();

  if (

    !allowUserSwitch &&

    current?.id != null &&

    snapshot.id != null &&

    Number(current.id) !== Number(snapshot.id)

  ) {

    return;

  }



  if (snapshot.role) {

    window.localStorage.setItem(ROLE_KEY, snapshot.role);

  }

  window.localStorage.setItem(

    SUPERUSER_KEY,

    snapshot.is_superuser ? "1" : "0"

  );

  if (snapshot.id != null) {

    window.localStorage.setItem(USER_ID_KEY, String(snapshot.id));

  }

  if (snapshot.company?.id != null) {

    window.localStorage.setItem(COMPANY_ID_KEY, String(snapshot.company.id));

  }

  window.localStorage.setItem(USER_JSON_KEY, JSON.stringify(snapshot));

  window.dispatchEvent(new Event("repman:session-changed"));

}



export function clearAuthUser() {

  if (typeof window === "undefined") return;

  window.localStorage.removeItem(ROLE_KEY);

  window.localStorage.removeItem(SUPERUSER_KEY);

  window.localStorage.removeItem(USER_ID_KEY);

  window.localStorage.removeItem(USER_JSON_KEY);

  window.localStorage.removeItem(COMPANY_ID_KEY);

  clearImpersonationState();

  window.dispatchEvent(new Event("repman:session-changed"));

}



export function setImpersonationState(impersonation) {

  if (typeof window === "undefined" || !impersonation?.active) return;

  window.localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(impersonation));

}



export function getImpersonationState() {

  if (typeof window === "undefined") return null;

  const raw = window.localStorage.getItem(IMPERSONATION_KEY);

  if (!raw) return null;

  try {

    const parsed = JSON.parse(raw);

    return parsed?.active ? parsed : null;

  } catch {

    return null;

  }

}



export function clearImpersonationState() {

  if (typeof window === "undefined") return;

  window.localStorage.removeItem(IMPERSONATION_KEY);

}



/** Apply login / impersonation API response to local session. */

export function applyAuthResponse(data) {

  if (typeof window === "undefined" || !data) return;



  if (data.access) {

    window.localStorage.setItem("repman_access_token", data.access);

  }



  const user = data.user;

  if (user) {

    persistAuthUser(user, { allowUserSwitch: true });

    const companyId = user.company?.id ?? user.company_id;

    if (companyId != null) {

      window.localStorage.setItem(COMPANY_ID_KEY, String(companyId));

    }

  }



  if (data.impersonation?.active) {

    setImpersonationState(data.impersonation);

  } else if (data.impersonation && !data.impersonation.active) {

    clearImpersonationState();

  }

}



export function getStoredRole() {

  if (typeof window === "undefined") return "";

  return window.localStorage.getItem(ROLE_KEY) || "";

}



/** Client dashboard access while impersonating, regardless of stored admin role. */

export function getEffectiveDashboardRole() {

  if (getImpersonationState()?.active) {

    return USER_ROLE.CLIENT;

  }

  return getStoredRole();

}



/** Preserve session before a full-page OAuth redirect (same browser origin). */

export function saveOAuthSessionCheckpoint(oauthState = null) {

  if (typeof window === "undefined") return;



  const checkpoint = {

    role: window.localStorage.getItem(ROLE_KEY),

    userJson: window.localStorage.getItem(USER_JSON_KEY),

    companyId: window.localStorage.getItem(COMPANY_ID_KEY),

    superuser: window.localStorage.getItem(SUPERUSER_KEY),

    impersonation: window.localStorage.getItem(IMPERSONATION_KEY),

    accessToken: window.localStorage.getItem("repman_access_token"),

    origin: window.location.origin,

    oauthState,

    savedAt: Date.now(),

  };



  writeOAuthCheckpointPayload(checkpoint, oauthState);

}



/** Restore session when Google redirects back with code/state (runs before React mounts). */

export function restoreOAuthSessionCheckpointIfNeeded() {

  if (typeof window === "undefined") return false;



  const params = new URLSearchParams(window.location.search);

  const oauthState = params.get("state");

  if (!params.get("code") || !oauthState) return false;



  const checkpoint = readOAuthCheckpointPayload(oauthState);

  if (!checkpoint) return false;



  return applyOAuthCheckpointPayload(checkpoint);

}



/** Re-apply saved client session after OAuth API calls (guards against refresh cookie drift). */

export function reconcileOAuthSession(oauthState) {

  if (typeof window === "undefined" || !oauthState) return false;



  const checkpoint = readOAuthCheckpointPayload(oauthState);

  if (!checkpoint) return false;



  const applied = applyOAuthCheckpointPayload(checkpoint);

  if (!applied) return false;



  const storedUserId = checkpoint.userJson

    ? (() => {

        try {

          return JSON.parse(checkpoint.userJson)?.id ?? null;

        } catch {

          return null;

        }

      })()

    : null;

  const tokenUserId = getUserIdFromAccessToken(

    window.localStorage.getItem("repman_access_token")

  );



  return !storedUserId || !tokenUserId || Number(storedUserId) === Number(tokenUserId);

}



export function clearOAuthSessionCheckpoint(oauthState = null) {

  if (typeof window === "undefined") return;

  window.sessionStorage.removeItem(OAUTH_CHECKPOINT_KEY);

  const stateKey = oauthCheckpointStorageKey(oauthState);

  if (stateKey) {

    window.localStorage.removeItem(stateKey);

  }

}



/**

 * If a token refresh returned a different user, restore the OAuth checkpoint session.

 * Returns true when a checkpoint restore was applied.

 */

export function recoverSessionFromOAuthCheckpointIfNeeded(refreshedUserId) {
  if (typeof window === "undefined" || refreshedUserId == null) return false;

  const storedUserId = getCurrentUser()?.id;
  if (storedUserId == null || Number(storedUserId) === Number(refreshedUserId)) {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  const oauthState = params.get("state");
  let checkpoint = readOAuthCheckpointPayload(oauthState);
  if (!checkpoint) {
    checkpoint = readOAuthCheckpointPayload(null);
  }
  if (!checkpoint) return false;

  const checkpointUserId = checkpoint.userJson
    ? (() => {
        try {
          return JSON.parse(checkpoint.userJson)?.id ?? null;
        } catch {
          return null;
        }
      })()
    : null;

  if (
    !checkpointUserId ||
    Number(checkpointUserId) !== Number(storedUserId)
  ) {
    return false;
  }

  return applyOAuthCheckpointPayload(checkpoint);
}



export function getStoredIsSuperuser() {

  if (typeof window === "undefined") return false;

  return window.localStorage.getItem(SUPERUSER_KEY) === "1";

}



/**

 * Returns the logged-in user snapshot from session storage (login / profile).

 * Used for company-scoped platform connections and similar client features.

 */

export function getCurrentUser() {

  if (typeof window === "undefined") return null;



  const raw = window.localStorage.getItem(USER_JSON_KEY);

  if (raw) {

    try {

      const parsed = JSON.parse(raw);

      if (parsed && (parsed.id != null || parsed.company?.id != null)) {

        return parsed;

      }

    } catch {

      // fall through to legacy keys

    }

  }



  const id = window.localStorage.getItem(USER_ID_KEY);

  const companyId = window.localStorage.getItem(COMPANY_ID_KEY);

  const role = getStoredRole();



  if (!id && !companyId && !role) {

    return null;

  }



  return {

    id: id ? Number(id) : undefined,

    role: role || undefined,

    is_superuser: getStoredIsSuperuser(),

    company: companyId ? { id: Number(companyId) } : null,

  };

}



export function isAdminRole(role) {

  return role === USER_ROLE.ADMIN;

}



export function isClientRole(role) {

  return role === USER_ROLE.CLIENT;

}


